import { request } from '@umijs/max';

// ==================== 网站设置（无需登录） ====================

/** 获取网站设置（根据域名） */
export async function getSiteSetting(domain: string) {
  return request<GEO.ApiResponse<{
    site_name: string;
    site_keywords: string;
    site_desc: string;
    site_logo: string;
    site_favicon: string;
    icp: string;
    copyright: string;
    contact_wechat: string;
    contact_phone: string;
    contact_qrcode: string;
    is_oem: boolean;
    oem_id: number;
  }>>('/api/site/getSetting', {
    method: 'GET',
    params: { domain },
  });
}

// ==================== 登录认证 ====================

/** 用户登录 */
export async function login(params: {
  username: string;
  password: string;
  captcha_key: string;
  captcha_code: string;
  /** 当前站点域名，与 getSiteSetting 一致，便于匹配 OEM 系统设置 */
  domain?: string;
}) {
  return request<GEO.ApiResponse<{
    token: string;
    user_info: GEO.UserInfo;
    site_name: string;
    site_logo: string;
    site_favicon: string;
    icp: string;
    copyright: string;
    package_expired?: boolean;
    has_active_package?: boolean;
    package_end_time?: number;
  }>>('/api/login', {
    method: 'POST',
    data: params,
  });
}

/** 获取当前用户信息 */
export async function getCurrentUser() {
  return request<GEO.ApiResponse<GEO.UserInfo>>('/api/currentUser', {
    method: 'GET',
  });
}

/** 获取登录验证码 */
export async function getCaptcha() {
  return request('/api/login/captcha', {
    method: 'GET',
  });
}

/** 分享数据（公开接口，无需登录） */
export async function getInclusionShare(params: {
  code: string;
  user_id?: number;
  platform_id?: number;
  page?: number;
  page_size?: number;
  keyword?: string;
  is_included?: number;
}) {
  return request<GEO.ApiResponse<GEO.ShareData>>('/api/external/getInclusionShare', {
    method: 'GET',
    params,
    publicApi: true,
    skipErrorHandler: true,
  });
}

// ==================== 首页数据 ====================

/** 获取首页数据 */
export async function getDashboard() {
  return request<GEO.ApiResponse<{
    ai_create_count: number;
    publish_count: number;
    keyword_count: number;
    inclusion_count: number;
    balance: number;
    token_balance: number;
    weekly_token_usage: Array<{ date: string; usage: number }>;
    keyword_query_stats?: {
      platforms: Array<{
        platform_id: number;
        platform_name: string;
        today_queried_count: number;
        today_pending_count: number;
        included_count: number;
        today_task_total: number;
        completion_rate: number;
      }>;
      totals: {
        today_queried_count: number;
        today_pending_count: number;
        included_count: number;
        today_task_total: number;
        completion_rate: number;
      };
    };
  }>>('/api/index/dashboard', {
    method: 'GET',
  });
}

// ==================== 基础配置 ====================

/** 获取基础配置 */
export async function getBaseConfig() {
  return request<GEO.ApiResponse<{
    id: number;
    avatar: string;
    company_name: string;
    inclusion_name: string;
    company_intro: string;
    contact_name: string;
    contact_phone: string;
    contact_wechat: string;
    website: string;
    address: string;
    registered_address: string;
    sales_amount: string;
    staff_size: string;
    company_desc: string;
    product_desc: string;
    user_group?: string;
    applicable_scenario?: string;
    qualification: string[];
  }>>('/api/config/get', {
    method: 'GET',
  });
}

/** 更新基础配置 */
export async function updateBaseConfig(data: {
  avatar?: string;
  inclusion_name?: string;
  contact_name?: string;
  contact_phone?: string;
  contact_wechat?: string;
  website?: string;
  address?: string;
  registered_address?: string;
  sales_amount?: string;
  staff_size?: string;
  company_desc: string;
  product_desc: string;
  user_group?: string;
  applicable_scenario?: string;
  qualification?: string[];
}) {
  return request<GEO.ApiResponse>('/api/config/update', {
    method: 'POST',
    data,
  });
}

/** 上传头像 */
export async function uploadAvatar(file: File) {
  const formData = new FormData();
  formData.append('file', file);
  return request<GEO.ApiResponse<{ url: string }>>('/api/config/uploadAvatar', {
    method: 'POST',
    data: formData,
  });
}

// ==================== 关键词管理 ====================

/** 获取关键词列表 */
export async function getKeywordList(params?: {
  type?: 1 | 2;
  page?: number;
  page_size?: number;
}) {
  return request<GEO.ApiResponse<{
    list: Array<{
      id: number;
      keyword: string;
      keyword_zh?: string;
      type: number;
      category: number;
      parent_id?: number;
      source_article_id?: number;
      type_display?: string;
      create_time: number;
    }>;
    total: number;
  }>>('/api/keyword/list', {
    method: 'GET',
    params,
  });
}

/** 搜索关键词 */
export async function getKeywordSearch(params?: {
  type?: 1 | 2;
  keyword?: string;
  page?: number;
  page_size?: number;
}) {
  return request<GEO.ApiResponse<{
    list: Array<{
      id: number;
      keyword: string;
      keyword_zh?: string;
      type: number;
    }>;
    total: number;
  }>>('/api/keyword/search', {
    method: 'GET',
    params,
  });
}

/** 获取关键词配比 */
export async function getRatio() {
  return request('/api/keyword/getRatio', {
    method: 'GET',
  });
}

/** 添加关键词 */
export async function addKeyword(data: { keyword: string}) {
  return request<GEO.ApiResponse<{ id: number }>>('/api/keyword/add', {
    method: 'POST',
    data,
  });
}


/** 批量添加关键词 */
export async function addBatchKeyword(data: {
  content: string; // 新模式：每行一个关键词
  type?: 2; // 批量导入仅支持拓展词
  category?: 1 | 2 | 3 | 4 | 5; // 拓展词分类
  parent_id?: number; // 拓展词所属主词ID（parent_id=0 的主词）
}) {
  return request<GEO.ApiResponse<{
    success_count: number;
    fail_count: number;
    total_count: number;
    errors?: string[];
  }>>('/api/keyword/batchAdd', {
    method: 'POST',
    data,
  });
}

/** AI拓词 */
export async function extendKeyword(keyword_id: number, count: number = 100) {
  return request<GEO.ApiResponse<{
    count: number;
    keywords: string[];
  }>>('/api/keyword/extend', {
    method: 'POST',
    data: { keyword_id, count },
  });
}

/** 设置关键词配比 */
export async function setKeywordRatio(data: {
  industry_ratio: number;
  question_ratio: number;
  scene_ratio: number;
  conversion_ratio: number;
}) {
  return request<GEO.ApiResponse>('/api/keyword/setRatio', {
    method: 'POST',
    data,
  });
}

/** 删除关键词 */
export async function deleteKeyword(id: number) {
  return request<GEO.ApiResponse>('/api/keyword/delete', {
    method: 'POST',
    data: { id },
  });
}

/** 批量删除关键词 */
export async function batchDeleteKeyword(ids: number[]) {
  return request<GEO.ApiResponse<{ deleted_count: number }>>('/api/keyword/delete', {
    method: 'POST',
    data: { ids },
  });
}

// ==================== 图片管理 ====================

/** 获取图片列表 */
export async function getImageList(params?: {
  keyword_id?: number;
  source?: number;
  status?: number;
  /** 多状态，如 "1,2" */
  status_in?: string;
  keyword?: string;
  is_check?: number;
  page?: number;
  page_size?: number;
}) {
  return request<GEO.ApiResponse<{
    list: Array<{
      id: number;
      title: string;
      url: string;
      source: number;
      is_check: number;
      create_time: number;
      keyword_id?: number;
      keyword_name?: string;
    }>;
    total: number;
  }>>('/api/image/list', {
    method: 'GET',
    params,
  });
}

/** AI生成图片 */
export async function generateImage(data: { keyword_id?: number; count?: number; prompt: string }) {
  return request<GEO.ApiResponse<{
    id: number;
    images: Array<{ id: number; url: string }>;
  }>>('/api/image/generate', {
    method: 'POST',
    data,
  });
}

/** 上传图片 */
export async function uploadImage(file: File, data?: { keyword_id?: number; title?: string }) {
  const formData = new FormData();
  formData.append('file', file);
  if (data?.keyword_id) formData.append('keyword_id', String(data.keyword_id));
  if (data?.title) formData.append('title', data.title);
  return request<GEO.ApiResponse<{
    id: number;
    url: string;
    title: string;
    keyword_id: number;
    source: number;
    create_time: number;
  }>>('/api/image/upload', {
    method: 'POST',
    data: formData,
  });
}

/** 删除图片 */
export async function deleteImage(id: number) {
  return request<GEO.ApiResponse>('/api/image/delete', {
    method: 'POST',
    data: { id },
  });
}

/** 更新图片审核状态 */
export async function updateImageCheck(id: number, is_check: 0 | 1) {
  return request<GEO.ApiResponse>('/api/image/updateCheck', {
    method: 'POST',
    data: { id, is_check },
  });
}

// ==================== 视频管理 ====================

/** 获取视频列表 */
export async function getVideoList(params?: {
  keyword_id?: number;
  type?: 1 | 2;
  source?: number;
  status?: number;
  keyword?: string;
  is_check?: number;
  page?: number;
  page_size?: number;
}) {
  return request<GEO.ApiResponse<{
    list: Array<{
      id: number;
      keyword: string;
      type: number;
      source: number;
      status: number;
      is_check: number;
      is_publish: number;
    }>;
    total: number;
  }>>('/api/video/list', {
    method: 'GET',
    params,
  });
}

/** AI生成视频 */
export async function generateVideo(data: {
  keyword_id: number;
  type: 1 | 2;
  title?: string;
  content?: string;
  prompt: string;
  count?: number;
  duration?: number;
}) {
  return request<GEO.ApiResponse<{
    id: number;
    tasks: Array<{ id: number; task_id: string }>;
  }>>('/api/video/generate', {
    method: 'POST',
    data,
  });
}

/** 查询视频生成结果并回写 */
export async function queryVideoGenerateResult(data: { video_id: number; task_id: string }) {
  return request<GEO.ApiResponse<{
    video_id: number;
    task_id: string;
    status: string;
    finished: boolean;
    failed: boolean;
    url: string;
    cover: string;
    error_msg: string;
  }>>('/api/video/queryGenerateResult', {
    method: 'POST',
    data,
  });
}

/** 上传视频 */
export async function uploadVideo(
  file: File,
  data: {
    keyword_id?: number;
    title?: string;
    content?: string;
    cover?: File;
  },
) {
  const formData = new FormData();
  formData.append('file', file);
  if (data.keyword_id) formData.append('keyword_id', String(data.keyword_id));
  if (data.title) formData.append('title', data.title);
  if (data.content) formData.append('content', data.content);
  if (data.cover) formData.append('cover', data.cover);
  return request<GEO.ApiResponse<{
    id: number;
    url: string;
    cover: string;
    title: string;
    content: string;
    keyword_id: number;
    source: number;
    create_time: number;
  }>>('/api/video/upload', {
    method: 'POST',
    data: formData,
  });
}

/** 删除视频 */
export async function deleteVideo(id: number) {
  return request<GEO.ApiResponse>('/api/video/delete', {
    method: 'POST',
    data: { id },
  });
}

/** 更新视频审核状态 */
export async function updateVideoCheck(id: number, is_check: 0 | 1) {
  return request<GEO.ApiResponse>('/api/video/updateCheck', {
    method: 'POST',
    data: { id, is_check },
  });
}

/** 更新视频发布状态 */
export async function updateVideoPublish(id: number, is_publish: 0 | 1) {
  return request<GEO.ApiResponse>('/api/video/updatePublish', {
    method: 'POST',
    data: { id, is_publish },
  });
}

// ==================== 文章管理 ====================

/** 获取文章列表 */
export async function getArticleList(params?: {
  keyword_id?: number;
  type?: 1 | 2;
  source?: number;
  status?: number;
  keyword?: string;
  is_check?: number;
  /** 0未发布 1已发布 */
  is_publish?: number;
  page?: number;
  page_size?: number;
}) {
  return request<GEO.ApiResponse<{
    list: Array<{
      id: number;
      title: string;
      title_zh?: string;
      content?: string;
      content_zh?: string;
      keyword_id?: number;
      command_id?: number;
      command_name?: string;
      type: number;
      source: number;
      status: number;
      is_check: number;
      is_publish: number;
      keyword_name?: string;
    }>;
    total: number;
  }>>('/api/article/list', {
    method: 'GET',
    params,
  });
}

/** 文章 AI 生成：可选内容指令列表 */
export async function getArticleCommandList() {
  return request<GEO.ApiResponse<{ list: Array<{ id: number; title: string }> }>>('/api/article/commands', {
    method: 'GET',
  });
}

/** 用户端：文章 AI 指令管理列表（仅用户自建） */
export async function userArticleCommandManageList(params?: {
  page?: number;
  page_size?: number;
  title?: string;
  status?: number;
}) {
  return request<GEO.ApiResponse<{ list: any[]; total: number }>>('/api/article/commandManage/list', {
    method: 'GET',
    params,
  });
}

/** 用户端：新增自建文章指令 */
export async function userArticleCommandManageAdd(data: {
  title: string;
  content: string;
  status?: number;
  sort_order?: number;
}) {
  return request<GEO.ApiResponse<{ id: number }>>('/api/article/commandManage/add', {
    method: 'POST',
    data,
  });
}

/** 用户端：更新自建文章指令 */
export async function userArticleCommandManageUpdate(data: {
  id: number;
  title?: string;
  content?: string;
  status?: number;
  sort_order?: number;
}) {
  return request<GEO.ApiResponse>('/api/article/commandManage/update', {
    method: 'POST',
    data,
  });
}

/** 用户端：删除自建文章指令 */
export async function userArticleCommandManageDelete(id: number) {
  return request<GEO.ApiResponse>('/api/article/commandManage/delete', {
    method: 'POST',
    data: { id },
  });
}

/** AI生成文章（内容指令/自定义；占位符在服务端替换；标题由正文生成后固定指令二次请求） */
export async function generateArticle(data: {
  keyword_ids: number[];
  type: 1 | 2;
  content_command_id?: number;
  /** 兼容旧版：等同内容指令 id */
  command_id?: number;
  custom_command?: string;
}) {
  return request<GEO.ApiResponse<{
    task_id: number;
    ids: number[];
    count: number;
  }>>('/api/article/generate', {
    method: 'POST',
    data,
  });
}

/** 上传文章（富文本 HTML，title/content 为英文，title_zh/content_zh 为中文） */
export async function uploadArticleManual(data: {
  keyword_id: number;
  title?: string;
  content?: string;
  title_zh?: string;
  content_zh?: string;
  type: 1 | 2;
}) {
  return request<GEO.ApiResponse<{ id: number }>>('/api/article/upload', {
    method: 'POST',
    data,
  });
}

/** 文章富文本编辑器：内嵌图片上传 */
/** 媒体发稿富文本内嵌视频上传 */
export async function uploadMediaReleaseEditorVideo(file: File) {
  const formData = new FormData();
  formData.append('file', file);
  return request<GEO.ApiResponse<{ url: string }>>('/api/mediaRelease/uploadEditorVideo', {
    method: 'POST',
    data: formData,
  });
}

/** 修改文章标题与正文（title/content 为英文，title_zh/content_zh 为中文） */
export async function updateArticle(data: {
  id: number;
  title?: string;
  content?: string;
  title_zh?: string;
  content_zh?: string;
}) {
  return request<GEO.ApiResponse<{ id: number }>>('/api/article/update', {
    method: 'POST',
    data,
  });
}

/** 删除文章 */
export async function deleteArticle(id: number) {
  return request<GEO.ApiResponse>('/api/article/delete', {
    method: 'POST',
    data: { id },
  });
}

/** 更新文章审核状态 */
export async function updateArticleCheck(id: number, is_check: 0 | 1) {
  return request<GEO.ApiResponse>('/api/article/updateCheck', {
    method: 'POST',
    data: { id, is_check },
  });
}

/** 批量更新文章审核状态 */
export async function batchUpdateArticleCheck(ids: number[], is_check: 0 | 1) {
  return request<GEO.ApiResponse<{ affected: number }>>('/api/article/batchUpdateCheck', {
    method: 'POST',
    data: { ids, is_check },
  });
}

/** 媒体发稿：媒体平台列表（本地缓存，需先执行 media-release:sync-media） */
export async function getMediaReleaseMediaList(data: {
  page?: number;
  limit?: number;
  id?: number;
  media_name?: string;
}) {
  return request<GEO.ApiResponse<Record<string, unknown>>>('/api/mediaRelease/mediaList', {
    method: 'POST',
    data,
  });
}

/** 媒体发稿：自媒体列表（本地缓存，搜索 toutiao_name） */
export async function getWeMediaReleaseMediaList(data: {
  page?: number;
  limit?: number;
  id?: number;
  toutiao_name?: string;
}) {
  return request<GEO.ApiResponse<Record<string, unknown>>>('/api/mediaRelease/weMediaList', {
    method: 'POST',
    data,
  });
}

/** 媒体发稿：海外媒体列表（本地缓存） */
export async function getOverseasMediaReleaseMediaList(data: {
  page?: number;
  limit?: number;
  id?: number;
  media_name?: string;
}) {
  return request<GEO.ApiResponse<Record<string, unknown>>>('/api/mediaRelease/overseasMediaList', {
    method: 'POST',
    data,
  });
}

/** 媒体发稿：稿件预览（公开，凭 token） */
export async function getMediaReleasePreview(token: string) {
  return request<GEO.ApiResponse<{
    title: string;
    content: string;
    preview_file?: string;
    create_time: number;
  }>>('/api/mediaRelease/preview', {
    method: 'GET',
    params: { token },
    skipErrorHandler: true,
  });
}

/** 媒体发稿：提交发布订单（服务端代签、代理 POST create_order） */
/** 媒体发稿订单列表 */
export async function getMediaReleaseOrderList(params?: {
  page?: number;
  page_size?: number;
  mode?: 'order2' | 'wemedia' | 'overseas';
}) {
  return request<GEO.ApiResponse<{
    list: MediaReleaseOrderItem[];
    total: number;
  }>>('/api/mediaRelease/orderList', {
    method: 'GET',
    params,
  });
}

/** 删除本系统发稿订单记录 */
export async function deleteMediaReleaseOrder(id: number) {
  return request<GEO.ApiResponse<null>>('/api/mediaRelease/deleteOrder', {
    method: 'POST',
    data: { id },
  });
}

/** 查询媒介盒子订单状态并更新本地记录 */
export async function queryMediaReleaseOrder(data: { id?: number; ids?: number[] }) {
  return request<GEO.ApiResponse<{
    list: MediaReleaseOrderItem[];
    raw: Record<string, unknown>;
  }>>('/api/mediaRelease/queryOrder', {
    method: 'POST',
    data,
  });
}

/** 查询当前用户所有待回传发稿订单（未处理/发布中） */
export async function queryPendingMediaReleaseOrders() {
  return request<GEO.ApiResponse<{
    list: MediaReleaseOrderItem[];
    raw: Record<string, unknown>;
    scanned: number;
    updated: number;
  }>>('/api/mediaRelease/queryPendingOrders', {
    method: 'POST',
  });
}

export type MediaReleaseOrderMode = 'wemedia' | 'order2' | 'overseas';

export type MediaReleaseOrderItem = {
  id: number;
  user_id: number;
  mode: MediaReleaseOrderMode | string;
  mode_label?: string;
  media_id: number;
  media_name: string;
  title: string;
  content?: string;
  article_type: number;
  publish_kind: string;
  xwy_ordernum: string;
  ordernum: string;
  order_status: number;
  order_status_label: string;
  published_at: string;
  publish_url: string;
  reject_reason: string;
  article_remark: string;
  source_article_id?: number;
  source_image_ids?: string;
  publish_synced?: number;
  publish_sync?: Record<string, unknown>;
  last_query_time?: number;
  last_query_time_text?: string;
  create_time?: number;
  create_time_text?: string;
  update_time?: number;
  /** 用户侧扣费金额（元） */
  charge_amount?: number;
  /** 扣费是否已返还：0否 1是 */
  charge_refunded?: number;
  charge_deductions?: Array<{
    user_id: number;
    amount: number;
    label?: string;
    finance_log_id?: number;
  }>;
};

export type MediaReleasePublishPayload = {
  mode: 'order2' | 'wemedia' | 'overseas';
  media_id: number;
  media_name?: string;
  title: string;
  content: string;
  /** 素材库文章 ID（订单完成后回写发布明细与 is_publish） */
  article_id?: number;
  /** 素材库配图 ID 列表 */
  image_ids?: number[];
  /** 自媒体 create_order 专用 */
  article_remark?: string;
  selling_price?: number;
  account_rule?: 1 | 2 | 3;
  article_type?: 1 | 2 | 3 | 4;
  allow_video?: 0 | 1 | 3;
  /** 文章/笔记：生成预览 HTML 后，提交媒介盒子的 content 仅为预览链接 */
  publish_kind?: 'article' | 'note';
  /** 自定义发布（媒体平台）：1=信源类 2=营销类 */
  material_article_type?: 1 | 2;
  /** @deprecated 预览链接域名由服务端固定为 API 域名，无需传入 */
  site_origin?: string;
  /** 海外媒体可选 */
  customer_name?: string;
};

export async function publishMediaRelease(data: MediaReleasePublishPayload) {
  return request<GEO.ApiResponse<Record<string, unknown>>>('/api/mediaRelease/publish', {
    method: 'POST',
    data,
  });
}

/** 媒体发稿：定时发布（按发布引擎「媒体文章发布」每日触发时间段） */
export async function scheduleMediaRelease(data: MediaReleasePublishPayload) {
  return request<GEO.ApiResponse<{
    id: number;
    scheduled_at: number;
    scheduled_at_text: string;
    publish_time_setting: string;
  }>>('/api/mediaRelease/schedulePublish', {
    method: 'POST',
    data,
  });
}

/** 预览下一次定时发布时间 */
export async function getMediaReleaseSchedulePreview() {
  return request<GEO.ApiResponse<{
    scheduled_at: number;
    scheduled_at_text: string;
    publish_time_setting: string;
  }>>('/api/mediaRelease/schedulePreview', {
    method: 'GET',
  });
}

/** 更新文章发布状态 */
/** 文章发布状态：0未发布 1已发布 2审核中 3审核拒绝 */
export async function updateArticlePublish(id: number, is_publish: 0 | 1 | 2 | 3) {
  return request<GEO.ApiResponse>('/api/article/updatePublish', {
    method: 'POST',
    data: { id, is_publish },
  });
}

export async function getArticleExpandKeywordsSetting() {
  return request<GEO.ApiResponse<{ enabled: boolean }>>('/api/article/expandKeywordsSetting', {
    method: 'GET',
  });
}

export async function updateArticleExpandKeywordsSetting(enabled: boolean) {
  return request<GEO.ApiResponse<{ enabled: boolean }>>('/api/article/updateExpandKeywordsSetting', {
    method: 'POST',
    data: { enabled: enabled ? 1 : 0 },
  });
}

// ==================== 成品库管理 ====================

/** 获取成品列表 */
export async function getProductList(params?: {
  material_type?: 1 | 2 | 3;
  audit_status?: 0 | 1 | 2;
  is_publishable?: 0 | 1;
  page?: number;
  page_size?: number;
}) {
  return request<GEO.ApiResponse<{
    list: Array<{
      id: number;
      material_type: number;
      title: string;
      content: string;
      audit_status: number;
      is_publishable: number;
      create_time: number;
    }>;
    total: number;
  }>>('/api/product/list', {
    method: 'GET',
    params,
  });
}

/** 预览成品 */
export async function previewProduct(id: number) {
  return request<GEO.ApiResponse<{
    id: number;
    material_type: number;
    title: string;
    content: string;
    url: string;
  }>>('/api/product/preview', {
    method: 'GET',
    params: { id },
  });
}

/** 删除成品 */
export async function deleteProduct(id: number) {
  return request<GEO.ApiResponse>('/api/product/delete', {
    method: 'POST',
    data: { id },
  });
}

/** 修改文章成品 */
export async function updateProduct(id: number, data: { title: string; content: string }) {
  return request<GEO.ApiResponse>('/api/product/update', {
    method: 'POST',
    data: { id, ...data },
  });
}

/** 标记可用 */
export async function markProductPublishable(id: number, is_publishable: 0 | 1) {
  return request<GEO.ApiResponse>('/api/product/markPublishable', {
    method: 'POST',
    data: { id, is_publishable },
  });
}

// ==================== 发布管理 ====================

/** 获取发布设置 */
export async function getPublishSetting() {
  return request<GEO.ApiResponse<{
    media_ids: number[];
    publish_time: string;
    frequency: number;
  }>>('/api/publish/getSetting', {
    method: 'GET',
  });
}

/** 获取发布平台（默认只拉启用且最多 100 条） */
export async function getMediaPlatform(params?: {
  status?: number;
  page?: number;
  page_size?: number;
  publish_category?: number;
}) {
  return request('/api/mediaPlatform/list', {
    method: 'GET',
    params: {
      status: 1,
      page: 1,
      page_size: 100,
      ...params,
    },
  });
}

/** 获取用户套餐数据 */
export async function getAssets() {
  return request('/api/asset/getAssets', {
    method: 'GET',
  });
}

/** 获取用户Token消耗记录 */
export async function getTokenLogs() {
  return request('/api/asset/tokenLogs', {
    method: 'GET',
  });
}

/** 获取AI平台 */
export async function getaiPlatform() {
  return request('/api/aiPlatform/list', {
    method: 'GET',
  });
}

// ==================== Admin OEM域名管理 ====================

/** 获取OEM域名列表 */
export async function getOemDomainList(params?: {
  oem_id?: number;
  status?: 0 | 1 | 2;
  domain?: string;
  page?: number;
  page_size?: number;
}) {
  return request<GEO.ApiResponse<{
    list: Array<{
      id: number;
      oem_id: number;
      domain: string;
      status: number;
      audit_time: number | null;
      create_time: number;
      oem: {
        id: number;
        company_name: string;
        username: string;
      };
    }>;
    total: number;
  }>>('/admin/oem/domainList', {
    method: 'GET',
    params,
  });
}

/** 审核OEM域名 */
export async function auditOemDomain(data: { id: number; status: 1 | 2 }) {
  return request<GEO.ApiResponse>('/admin/oem/auditDomain', {
    method: 'POST',
    data,
  });
}

/** 修改OEM域名 */
export async function updateOemDomain(data: {
  id: number;
  domain?: string;
  status?: 0 | 1 | 2;
}) {
  return request<GEO.ApiResponse>('/admin/oem/updateDomain', {
    method: 'POST',
    data,
  });
}

/** 删除OEM域名 */
export async function deleteOemDomain(id: number) {
  return request<GEO.ApiResponse>('/admin/oem/deleteDomain', {
    method: 'POST',
    data: { id },
  });
}

// ==================== Admin OEM网站设置 ====================

/** 获取OEM网站设置（Admin端） */
export async function getAdminOemSiteSetting(oem_id: number) {
  return request<GEO.ApiResponse<{
    user_id: number;
    company_name: string;
    site_name: string;
    site_keywords: string;
    site_desc: string;
    site_logo: string;
    site_favicon: string;
    icp: string;
    copyright: string;
    contact_wechat: string;
    contact_phone: string;
    contact_qrcode: string;
  }>>('/admin/oem/getSiteSetting', {
    method: 'GET',
    params: { oem_id },
  });
}

/** 修改OEM网站设置（Admin端） */
export async function updateAdminOemSiteSetting(data: {
  oem_id: number;
  site_name?: string;
  site_keywords?: string;
  site_desc?: string;
  site_logo?: string;
  site_favicon?: string;
  icp?: string;
  copyright?: string;
  contact_wechat?: string;
  contact_phone?: string;
  contact_qrcode?: string;
}) {
  return request<GEO.ApiResponse>('/admin/oem/updateSiteSetting', {
    method: 'POST',
    data,
  });
}

// ==================== Admin 超管网站设置 ====================

/** 获取超管网站设置 */
export async function getAdminSiteSetting() {
  return request<GEO.ApiResponse<{
    site_name: string;
    site_keywords: string;
    site_desc: string;
    site_logo: string;
    site_favicon: string;
    icp: string;
    copyright: string;
    contact_wechat: string;
    contact_phone: string;
    contact_qrcode: string;
  }>>('/admin/site/get', {
    method: 'GET',
  });
}

/** 修改超管网站设置 */
export async function updateAdminSiteSetting(data: {
  site_name?: string;
  site_keywords?: string;
  site_desc?: string;
  site_logo?: string;
  site_favicon?: string;
  icp?: string;
  copyright?: string;
  contact_wechat?: string;
  contact_phone?: string;
  contact_qrcode?: string;
}) {
  return request<GEO.ApiResponse>('/admin/site/update', {
    method: 'POST',
    data,
  });
}

/** 上传Logo */
export async function uploadAdminLogo(file: File) {
  const formData = new FormData();
  formData.append('file', file);
  return request<GEO.ApiResponse<{ url: string }>>('/admin/site/uploadLogo', {
    method: 'POST',
    data: formData,
  });
}

/** 上传微信二维码 */
export async function uploadAdminQrcode(file: File) {
  const formData = new FormData();
  formData.append('file', file);
  return request<GEO.ApiResponse<{ url: string }>>('/admin/site/uploadQrcode', {
    method: 'POST',
    data: formData,
  });
}

/** 通用图片上传 */
export async function uploadAdminImage(file: File) {
  const formData = new FormData();
  formData.append('file', file);
  return request<GEO.ApiResponse<{ url: string }>>('/admin/site/uploadImage', {
    method: 'POST',
    data: formData,
  });
}

/** 超管-文章AI指令列表 */
export async function adminArticleCommandList(params?: {
  page?: number;
  page_size?: number;
  title?: string;
  status?: number;
}) {
  return request<GEO.ApiResponse<{ list: any[]; total: number }>>('/admin/articleCommand/list', {
    method: 'GET',
    params,
  });
}

/** 超管-新增文章AI指令 */
export async function adminArticleCommandAdd(data: {
  title: string;
  content: string;
  status?: number;
  sort_order?: number;
}) {
  return request<GEO.ApiResponse<{ id: number }>>('/admin/articleCommand/add', {
    method: 'POST',
    data,
  });
}

/** 超管-更新文章AI指令 */
export async function adminArticleCommandUpdate(data: {
  id: number;
  title?: string;
  content?: string;
  status?: number;
  sort_order?: number;
}) {
  return request<GEO.ApiResponse>('/admin/articleCommand/update', {
    method: 'POST',
    data,
  });
}

/** 超管-删除文章AI指令 */
export async function adminArticleCommandDelete(id: number) {
  return request<GEO.ApiResponse>('/admin/articleCommand/delete', {
    method: 'POST',
    data: { id },
  });
}

/** 更新发布设置 */
export async function updatePublishSetting(data: {
  media_ids: number[] | string;
  publish_time?: string;
  frequency?: string | Record<string, any>;
}) {
  return request<GEO.ApiResponse>('/api/publish/updateSetting', {
    method: 'POST',
    data,
  });
}

/** 手动添加发布明细（无关联素材，product_id=0，默认发布成功） */
export async function createManualPublish(data: {
  title: string;
  keyword_id: number;
  /** geo_media_platforms.id（status=1） */
  platform_id: number;
  material_type: 1 | 2 | 3;
  publish_url?: string;
  /** Unix 秒或毫秒时间戳，或日期时间字符串 */
  publish_time: number | string;
  /** 0失败 1成功 2审核中 3审核拒绝，默认1 */
  status?: 0 | 1 | 2 | 3;
  /** 1自媒体发布 2B2B 3媒体发布；不传默认1 */
  publish_channel_type?: 1 | 2 | 3;
}) {
  return request<GEO.ApiResponse<{ id: number }>>('/api/publish/manualAdd', {
    method: 'POST',
    data,
  });
}

/** 更新发布明细（publish_url / publish_time / status） */
export async function updatePublishRecord(data: {
  id: number;
  publish_url?: string;
  publish_time?: number | string;
  status?: 0 | 1 | 2 | 3;
}) {
  return request<GEO.ApiResponse>('/api/publish/update', {
    method: 'POST',
    data,
  });
}

/** 上报素材发布结果（写入 publish_records） */
export async function updatePublishedMaterial(data: {
  material_type: 1 | 2 | 3;
  product_id: number;
  media_id: number;
  publish_url?: string;
  /** 0失败 1成功 2审核中 3审核拒绝，默认1 */
  status?: 0 | 1 | 2 | 3;
  /** 1自媒体发布 2B2B发布 3媒体发布，默认1 */
  publish_channel_type?: 1 | 2 | 3;
}) {
  return request<GEO.ApiResponse>('/api/publishAccount/updatePublished', {
    method: 'POST',
    data,
  });
}

/** 删除发布明细 */
export async function deletePublishRecord(id: number) {
  return request<GEO.ApiResponse>('/api/publish/delete', {
    method: 'POST',
    data: { id },
  });
}

/** 获取发布链接列表（product_id 为素材表主键，需结合 material_type：1图 2视频 3文章） */
export async function getPublishList(params?: {
  page?: number;
  page_size?: number;
  keyword?: string;
  platform_id?: number;
  material_type?: 1 | 2 | 3;
  /** 1自媒体发布 2B2B 3媒体发布 */
  publish_channel_type?: 1 | 2 | 3;
  /** 0失败 1成功 2审核中 3审核拒绝 */
  status?: 0 | 1 | 2 | 3;
}) {
  return request<GEO.ApiResponse<{
    list: Array<{
      id: number;
      user_id?: number;
      material_type: number;
      material_type_name?: string;
      product_id: number;
      material_id?: number;
      media_id: number;
      platform_id?: number;
      product_name: string;
      keyword_name: string;
      platform_name: string;
      account_name?: string;
      publish_url: string;
      publish_time: number;
      publish_channel_type?: number;
      publish_channel_type_name?: string;
      status: number;
      status_text?: string;
      create_time?: number;
    }>;
    total: number;
  }>>('/api/publish/list', {
    method: 'GET',
    params,
  });
}

/** 获取发布账号 */
export async function getPublishAccountList(params?: { page?: number; page_size?: number }) {
  return request<GEO.ApiResponse<{
    list: Array<{
      id: number;
      material_id: number;
      platform_name?: string;
      status: number;
      remark: string;
    }>;
    total: number;
  }>>('/api/publishAccount/list', {
    method: 'GET',
    params,
  });
}

/** 更新发布账号备注 */
export async function updatePublishAccountRemark(data: {
  id: number;
  remark?: string;
}) {
  return request<GEO.ApiResponse>('/api/publishAccount/updateRemark', {
    method: 'POST',
    data,
  });
}

/** 删除发布账号备注 */
export async function deletePublishAccount(data: {
  id: number;
}) {
  return request<GEO.ApiResponse>('/api/publishAccount/delete', {
    method: 'POST',
    data,
  });
}

/** 获取AI授权账号 */
export async function getAiAccountList(params?: { page?: number; page_size?: number }) {
  return request<GEO.ApiResponse<{
    list: Array<{
      material_id: number;
      status: number;
      remark: string;
    }>;
    total: number;
  }>>('/api/aiAccount/list', {
    method: 'GET',
    params,
  });
}

/** 更新AI授权账号备注 */
export async function updateAiAccountRemark(data: {
  id: number;
  remark?: string;
}) {
  return request<GEO.ApiResponse>('/api/aiAccount/updateRemark', {
    method: 'POST',
    data,
  });
}

/** 删除AI授权账号 */
export async function deleteAiAccount(data: {
  id: number;
}) {
  return request<GEO.ApiResponse>('/api/aiAccount/delete', {
    method: 'POST',
    data,
  });
}

// ==================== 财务管理 ====================

/** 获取余额 */
export async function getBalance() {
  return request<GEO.ApiResponse<{
    balance: string;
    token_balance: number;
  }>>('/api/finance/balance', {
    method: 'GET',
  });
}

/** 获取余额流水 */
export async function getFinanceLogs(params?: {
  type?: 1 | 2 | 3 | 4 | 5 | 6;
  amount_direction?: 'increase' | 'decrease';
  page?: number;
  page_size?: number;
}) {
  return request<GEO.ApiResponse<{
    list: Array<{
      id: number;
      type: number;
      amount: string;
      balance_before: string;
      balance_after: string;
      remark: string;
      create_time: number;
    }>;
    total: number;
    summary?: {
      total_increase: number;
      total_decrease: number;
    };
  }>>('/api/finance/logs', {
    method: 'GET',
    params,
  });
}

/** 获取套餐列表 */
export async function getPackageList(params?: { user_id?: number }) {
  return request<GEO.ApiResponse<{
    token?: {
      price: number;
      original_price?: number;
      default_price?: number;
      new_price?: string;
    };
    packages: Array<{
      id: number;
      name: string;
      type: number;
      duration: number;
      main_keywords: number;
      extend_keywords: number;
      source_articles: number;
      media_accounts: number;
      ai_accounts: number;
      price: number;
      original_price?: number;
      default_price?: number;
      new_price?: string;
    }>;
  }>>('/api/finance/packages', {
    method: 'GET',
    params,
  });
}

/** 购买套餐 */
export async function buyPackage(package_id: number) {
  return request<GEO.ApiResponse<{
    order_id: number;
    start_time: number;
    end_time: number;
  }>>('/api/finance/buyPackage', {
    method: 'POST',
    data: { package_id },
  });
}

/** 获取Token价格 */
export async function getTokenPrice() {
  return request<GEO.ApiResponse<{
    price: string;
    unit: string;
  }>>('/api/finance/tokenPrice', {
    method: 'GET',
  });
}

/** 购买Token */
export async function buyToken(amount: number) {
  return request<GEO.ApiResponse<{
    token_balance: number;
    cost: string;
  }>>('/api/finance/buyToken', {
    method: 'POST',
    data: { amount },
  });
}

// ==================== 数据报表 ====================

/** 获取收录数据 */
export async function getInclusionData(params?: {
  keyword_id?: number;
  keyword?: string;
  is_included?: 0 | 1;
  ai_platforms?: number;
  update_mode?: 1 | 2 | 3;
  page?: number;
  page_size?: number;
}) {
  return request<GEO.ApiResponse<{
    list: Array<{
      id: number;
      ai_platforms: number;
      user_id: number;
      keyword_id: number;
      keyword: string;
      main_keyword_name: string;
      is_included: number;
      check_time: number;
      update_mode: number;
      create_time: string;
      platform_name?: string;
      platform_url?: string;
    }>;
    total: number;
    platform_ratio: Array<{
      platform_id: number;
      platform_name: string;
      platform_url: string;
      total: number;
      included: number;
      ratio: number;
    }>;
  }>>('/api/report/inclusion', {
    method: 'GET',
    params,
  });
}

/** 自助查询收录 */
export async function checkInclusion(keyword_ids: number[], platform_ids: number[] = []) {
  return request<GEO.ApiResponse<{
    cost_token: number;
    results: Array<{
      keyword_id: number;
      keyword: string;
      is_included: number;
    }>;
  }>>('/api/report/checkInclusion', {
    method: 'POST',
    data: { keyword_ids, platform_ids },
  });
}

/** 提升当前用户收录队列查询优先级 */
export async function prioritizeUserJobs(priority = 101) {
  return request<GEO.ApiResponse<{
    user_id: number;
    priority: number;
    updated_items: number;
  }>>('/api/report/prioritizeUserJobs', {
    method: 'POST',
    data: { priority },
  });
}

/** 获取自助查询记录 */
export async function getInclusionCheckRecords(params?: {
  limit?: number;
  page?: number;
  page_size?: number;
  keyword?: string;
}) {
  return request<GEO.ApiResponse<{
    list: Array<{
      task_id: number;
      keyword_id: number;
      keyword: string;
      status: number;
      status_name: string;
      create_time: number | string;
      update_time: number | string;
      finish_time: number | string;
      is_included: number | null;
      platform_id: number;
      platform_name: string;
      platform_url: string;
      job_item_id: number;
    }>;
    total: number;
  }>>('/api/report/inclusionCheckRecords', {
    method: 'GET',
    params,
  });
}

// ==================== 案例管理 ====================

/** 获取案例列表 */
export async function getCaseList(params?: { page?: number; page_size?: number }) {
  return request<GEO.ApiResponse<{
    list: Array<{
      id: number;
      title: string;
      content: string;
      images: string[];
      create_time: number;
    }>;
    total: number;
  }>>('/api/case/list', {
    method: 'GET',
    params,
  });
}

/** 添加案例 */
export async function addCase(data: { title: string; content: string; images?: string[] }) {
  return request<GEO.ApiResponse<{ id: number }>>('/api/case/add', {
    method: 'POST',
    data,
  });
}

/** 更新案例 */
export async function updateCase(id: number, data: { title: string; content: string; images?: string[] }) {
  return request<GEO.ApiResponse>('/api/case/update', {
    method: 'POST',
    data: { id, ...data },
  });
}

/** 删除案例 */
export async function deleteCase(id: number) {
  return request<GEO.ApiResponse>('/api/case/delete', {
    method: 'POST',
    data: { id },
  });
}

// ==================== 代理端接口 ====================

/** 获取代理仪表盘数据 */
export async function getAgentDashboard() {
  return request<GEO.ApiResponse<{
    team_data: {
      user_count: number;
      ai_create_count: number;
      publish_count: number;
      keyword_count: number;
      inclusion_count: number;
    };
    weekly_data: {
      dates: string[];
      token_usage: number[];
      ai_create: number[];
      publish: number[];
      inclusion: number[];
    };
    sub_users: Array<{
      user_id: number;
      company_name: string;
      contact_name: string;
      contact_phone: string;
      balance: number;
      token_balance: number;
      ai_create_count: number;
      publish_count: number;
      keyword_count: number;
      inclusion_count: number;
      create_time: string;
      status: number;
    }>;
    keyword_query_stats?: {
      platforms: Array<{
        platform_id: number;
        platform_name: string;
        today_queried_count: number;
        today_pending_count: number;
        included_count: number;
        today_task_total: number;
        completion_rate: number;
      }>;
      totals: {
        today_queried_count: number;
        today_pending_count: number;
        included_count: number;
        today_task_total: number;
        completion_rate: number;
      };
    };
    token_price_per_million?: number;
    weekly_token_cost_yuan?: number;
  }>>('/agent/data/dashboard', {
    method: 'GET',
  });
}

/** 获取子用户数据 */
export async function getAgentUsersData() {
  return request<GEO.ApiResponse<{
    total_users: number;
    active_users: number;
    total_balance: string;
    total_token: number;
  }>>('/agent/data/users', {
    method: 'GET',
  });
}

/** 当前登录用户资料（与登录 user_info 一致） */
export async function getUserProfile() {
  return request<GEO.ApiResponse<GEO.UserInfo>>('/api/user/profile', {
    method: 'GET',
  });
}

/** 当前登录用户站点品牌（site_name / site_logo / site_favicon / icp / copyright，与登录解析规则一致） */
export async function getUserSiteBranding(domain?: string) {
  return request<GEO.ApiResponse<{
    site_name: string;
    site_logo: string;
    site_favicon: string;
    icp: string;
    copyright: string;
  }>>('/api/user/branding', {
    method: 'GET',
    params: domain ? { domain } : undefined,
  });
}

/** 代理端：登录下级账号（返回短时 token + user_info） */
export async function impersonateSubUser(params: { user_id: number }) {
  return request<GEO.ApiResponse<{ token: string; user_info: GEO.UserInfo }>>('/agent/user/impersonate', {
    method: 'POST',
    data: params,
  });
}

/** 获取设置价格 */
export async function getSetPackagePrices() {
  return request('/agent/user/packagePrices', {
    method: 'GET',
  });
}

/** 获取下级用户列表 */
export async function getAgentUserList(params?: {
  page?: number;
  page_size?: number;
  username?: string;
  company_name?: string;
  contact_name?: string;
  parent_name?: string;
  user_type?: 1 | 2 | 3 | 4;
  status?: number;
  balance?: 'asc' | 'desc';
  token_balance?: 'asc' | 'desc';
  create_time?: 'asc' | 'desc';
}) {
  return request<GEO.ApiResponse<{
    list: Array<{
      id: number;
      username: string;
      company_name: string;
      inclusion_name?: string;
      package_name?: string;
      contact_name: string;
      balance: string;
      token_balance: number;
      status: number;
      create_time: number;
    }>;
    total: number;
  }>>('/agent/user/list', {
    method: 'GET',
    params,
  });
}

/** 获取下级用户详情 */
export async function getUserDetail(params?: { user_id?: number }) {
  return request<GEO.ApiResponse<{
    user_info: {
      user_id: number;
      avatar?: string;
      company_name: string;
      company_intro?: string;
      inclusion_name?: string | null;
      contact_name: string;
      contact_phone: string;
      website?: string;
      address?: string;
      registered_address?: string;
      sales_amount?: string;
      staff_size?: string;
      company_desc?: string;
      product_desc?: string;
      qualification?: string;
      company_honor?: string;
      balance: number;
      token_balance: number;
      status: number;
      create_time: number;
    };
    package_data: Array<{
      package_id: number;
      package_name: string;
      price: number;
      start_time: number;
      end_time: number;
      main_keywords_used: number;
      extend_keywords_used: number;
      source_articles_used: number;
    }>;
    inclusion_data: {
      total: number;
      included: number;
      ratio: number;
    };
    create_data: {
      ai_image_count: number;
      ai_video_count: number;
      ai_article_count: number;
      ai_total: number;
      upload_image_count: number;
      upload_video_count: number;
      upload_total: number;
      publish_count: number;
    };
  }>>('/agent/user/detail', {
    method: 'GET',
    params,
  });
}

/** 代理端：修改用户公司资料信息 */
export async function updateAgentUserInfo(data: {
  user_id: number;
  parent_id?: number;
  company_name?: string;
  company_intro?: string;
  inclusion_name?: string;
  contact_name?: string;
  contact_phone?: string;
  website?: string;
  address?: string;
  registered_address?: string;
  sales_amount?: string;
  staff_size?: string;
  company_desc?: string;
  product_desc?: string;
  company_honor?: string;
}) {
  return request<GEO.ApiResponse>('/agent/user/updateInfo', {
    method: 'POST',
    data,
  });
}

/** 代理端：获取修改信息可选上级（级联） */
export async function getAgentParentOptions(params: { user_id: number }) {
  return request<GEO.ApiResponse<{
    options: Array<{
      value: number;
      label: string;
      children?: any[];
    }>;
    current_parent_id: number;
    selectable_ids: number[];
  }>>('/agent/user/parentOptions', {
    method: 'GET',
    params,
  });
}

/** 新增下级用户（含价格体系初始化） */
export async function addAgentUser(data: {
  company_name: string;
  password: string;
  user_type: number;
  contact_name?: string;
  contact_phone?: string;
  token_price?: number;
  package_prices?: Array<{
    package_id: number;
    price: number;
  }>;
}) {
  return request<GEO.ApiResponse<{ user_id: number }>>('/agent/user/add', {
    method: 'POST',
    data,
  });
}

/** 获取用户价格设置 */
export async function getUserPriceSettings(user_id: number) {
  return request<GEO.ApiResponse<{
    user_id: number;
    company_name: string;
    token_price: number;
    package_prices: Array<{
      package_id: number;
      package_name: string;
      price: number;
      original_price: number;
    }>;
  }>>('/agent/user/getPriceSettings', {
    method: 'GET',
    params: { user_id },
  });
}

/** 修改用户Token价格 */
export async function updateUserTokenPrice(data: {
  user_id: number;
  price: number;
}) {
  return request<GEO.ApiResponse<{
    user_id: number;
    token_price: number;
  }>>('/agent/user/updateTokenPrice', {
    method: 'POST',
    data,
  });
}

/** 修改用户套餐价格 */
export async function updateUserPackagePrice(data: {
  user_id: number;
  package_id: number;
  price: number;
}) {
  return request<GEO.ApiResponse<{
    user_id: number;
    package_id: number;
    package_name: string;
    price: number;
  }>>('/agent/user/updatePackagePrice', {
    method: 'POST',
    data,
  });
}

/** 批量修改用户价格设置 */
export async function batchUpdateUserPrices(data: {
  user_id: number;
  token_price?: number;
  package_prices?: Array<{
    package_id: number;
    price: number;
  }>;
}) {
  return request<GEO.ApiResponse<{
    user_id: number;
    token_price: number;
    package_prices: Array<{
      user_id: number;
      package_id: number;
      package_name: string;
      price: number;
    }>;
  }>>('/agent/user/batchUpdatePrices', {
    method: 'POST',
    data,
  });
}

/** 修改下级用户密码 */
export async function changePassword(data: {
  user_id: number;
  new_password: string;
}) {
  return request<GEO.ApiResponse<{ user_id: number }>>('/agent/user/changePassword', {
    method: 'POST',
    data,
  });
}

/** 修改自己的密码 */
export async function changeSelfPassword(data: {
  old_password: string;
  new_password: string;
}) {
  return request<GEO.ApiResponse<{ user_id: number }>>('/api/user/changePassword', {
    method: 'POST',
    data,
  });
}

/** 封禁用户 */
export async function changeBan(data: {
  user_id: number;
  status: 0 | 1;
}) {
  return request<GEO.ApiResponse<{ user_id: number }>>('/agent/user/ban', {
    method: 'POST',
    data,
  });
}

/** 增减用户余额 */
export async function adjustBalance(data: {
  user_id: number;
  amount: number;
  remark: string;
}) {
  return request<GEO.ApiResponse<{ user_id: number }>>('/agent/user/adjustBalance', {
    method: 'POST',
    data,
  });
}

/** 增减用户token余额 */
export async function adjustTokenBalance(data: {
  user_id: number;
  amount: number;
  remark: string;
}) {
  return request<GEO.ApiResponse<{ user_id: number }>>('/agent/user/adjustTokenBalance', {
    method: 'POST',
    data,
  });
}

/** 给用户开通套餐 */
export async function openPackage(data: {
  user_id: number;
  package_id: number;
}) {
  return request<GEO.ApiResponse<{ user_id: number }>>('/agent/user/openPackage', {
    method: 'POST',
    data,
  });
}

/** 获取Token价格设置 */
export async function getAgentTokenPrice() {
  return request<GEO.ApiResponse<{
    price: string;
    unit: string;
  }>>('/agent/price/token', {
    method: 'GET',
  });
}

/** 设置Token价格 */
export async function setAgentTokenPrice(price: number) {
  return request<GEO.ApiResponse>('/agent/price/setToken', {
    method: 'POST',
    data: { price },
  });
}

/** 获取套餐价格设置 */
export async function getAgentPackagePrices() {
  return request<GEO.ApiResponse<Array<{
    package_id: number;
    package_name: string;
    price: string;
  }>>>('/agent/price/packages', {
    method: 'GET',
  });
}

/** 设置套餐价格 */
export async function setAgentPackagePrice(package_id: number, price: number) {
  return request<GEO.ApiResponse>('/agent/price/setPackage', {
    method: 'POST',
    data: { package_id, price },
  });
}

// ==================== OEM端接口 ====================

/** 获取网站设置 */
export async function getOemSiteSetting() {
  return request<GEO.ApiResponse<{
    site_name: string;
    site_keywords: string;
    site_desc: string;
    site_logo: string;
    site_favicon: string;
    icp: string;
    copyright: string;
    contact_wechat: string;
    contact_phone: string;
    contact_qrcode: string;
  }>>('/oem/site/get', {
    method: 'GET',
  });
}

/** 更新网站设置 */
export async function updateOemSiteSetting(data: {
  site_name?: string;
  site_keywords?: string;
  site_desc?: string;
  site_logo?: string;
  site_favicon?: string;
  icp?: string;
  copyright?: string;
  contact_wechat?: string;
  contact_phone?: string;
  contact_qrcode?: string;
}) {
  return request<GEO.ApiResponse>('/oem/site/update', {
    method: 'POST',
    data,
  });
}

/** 上传图片 */
export async function uploadOemImage(file: File) {
  const formData = new FormData();
  formData.append('file', file);
  return request<GEO.ApiResponse<{ url: string }>>('/oem/site/uploadImage', {
    method: 'POST',
    data: formData,
  });
}

/** 提交域名授权 */
export async function submitOemDomain(domain: string) {
  return request<GEO.ApiResponse>('/oem/auth/submitDomain', {
    method: 'POST',
    data: { domain },
  });
}

/** 获取授权状态 */
export async function getOemAuthStatus() {
  return request<GEO.ApiResponse<{
    domain: string;
    status: number;
    status_text: string;
  }>>('/oem/auth/getStatus', {
    method: 'GET',
  });
}

/** 获取联系设置 */
export async function getOemContactSetting() {
  return request<GEO.ApiResponse<{
    contact_wechat: string;
    contact_phone: string;
    contact_qrcode: string;
  }>>('/oem/contact/get', {
    method: 'GET',
  });
}

/** 更新联系设置 */
export async function updateOemContactSetting(data: {
  contact_wechat?: string;
  contact_phone?: string;
  contact_qrcode?: string;
}) {
  return request<GEO.ApiResponse>('/oem/setting/updateContact', {
    method: 'POST',
    data,
  });
}

// ==================== 超管端接口 ====================

/** 获取普通用户列表 */
export async function getAdminUserList(params?: { page?: number; page_size?: number }) {
  return request<GEO.ApiResponse<{
    list: any[];
    total: number;
  }>>('/admin/user/list', {
    method: 'GET',
    params,
  });
}

/** 获取普通用户列表 */
export async function getPackagePrices(params?: { page?: number; page_size?: number }) {
  return request<GEO.ApiResponse<{
    list: any[];
    total: number;
  }>>('/admin/user/packagePrices', {
    method: 'GET',
    params,
  });
}

/** 新增普通用户 */
export async function addAdminUser(data: {
  username: string;
  password: string;
  parent_id?: number;
}) {
  return request<GEO.ApiResponse<{ user_id: number }>>('/admin/user/add', {
    method: 'POST',
    data,
  });
}

/** 修改用户余额 */
export async function modifyUserBalance(data: {
  id: number;
  amount: number;
  remark?: string;
}) {
  return request<GEO.ApiResponse<{ new_balance: string }>>('/admin/user/modifyBalance', {
    method: 'POST',
    data,
  });
}

/** 获取代理用户列表 */
export async function getAdminAgentList(params?: { page?: number; page_size?: number }) {
  return request<GEO.ApiResponse<{
    list: any[];
    total: number;
  }>>('/admin/agent/list', {
    method: 'GET',
    params,
  });
}

/** 新增代理用户 */
export async function addAdminAgent(data: {
  username: string;
  password: string;
  parent_id?: number;
}) {
  return request<GEO.ApiResponse<{ agent_id: number }>>('/admin/agent/add', {
    method: 'POST',
    data,
  });
}

/** 获取OEM列表 */
export async function getAdminOemList(params?: { page?: number; page_size?: number }) {
  return request<GEO.ApiResponse<{
    list: any[];
    total: number;
  }>>('/admin/oem/list', {
    method: 'GET',
    params,
  });
}

// ==================== Admin 套餐管理 ====================

/** 套餐数据类型 */
export interface PackageItem {
  id: number;
  name: string;
  type: number;
  type_text: string;
  duration: number;
  duration_text: string;
  main_keywords: number;
  extend_keywords: number;
  source_articles: number;
  media_accounts: number;
  ai_accounts: number;
  price: string;
  status: number;
  status_text: string;
  create_time: number;
  update_time?: number;
}

/** 获取套餐列表 */
export async function getAdminPackageList(params?: {
  type?: 1 | 2;
  status?: 0 | 1;
  name?: string;
  page?: number;
  page_size?: number;
}) {
  return request<GEO.ApiResponse<{
    list: PackageItem[];
    total: number;
  }>>('/admin/package/list', {
    method: 'GET',
    params,
  });
}

/** 获取套餐详情 */
export async function getAdminPackageDetail(id: number) {
  return request<GEO.ApiResponse<PackageItem>>('/admin/package/detail', {
    method: 'GET',
    params: { id },
  });
}

/** 获取所有启用套餐 */
export async function getAdminPackageAll() {
  return request<GEO.ApiResponse<PackageItem[]>>('/admin/package/all', {
    method: 'GET',
  });
}

/** 新增套餐 */
export async function addAdminPackage(data: {
  name: string;
  type: 1 | 2;
  duration: number;
  main_keywords?: number;
  extend_keywords?: number;
  source_articles?: number;
  media_accounts?: number;
  ai_accounts?: number;
  price?: number;
  status?: 0 | 1;
}) {
  return request<GEO.ApiResponse>('/admin/package/add', {
    method: 'POST',
    data,
  });
}

/** 修改套餐 */
export async function updateAdminPackage(data: {
  id: number;
  name?: string;
  type?: 1 | 2;
  duration?: number;
  main_keywords?: number;
  extend_keywords?: number;
  source_articles?: number;
  media_accounts?: number;
  ai_accounts?: number;
  price?: number;
  status?: 0 | 1;
}) {
  return request<GEO.ApiResponse>('/admin/package/update', {
    method: 'POST',
    data,
  });
}

/** 删除套餐 */
export async function deleteAdminPackage(id: number) {
  return request<GEO.ApiResponse>('/admin/package/delete', {
    method: 'POST',
    data: { id },
  });
}

/** 启用/禁用套餐 */
export async function toggleAdminPackageStatus(data: { id: number; status: 0 | 1 }) {
  return request<GEO.ApiResponse>('/admin/package/toggleStatus', {
    method: 'POST',
    data,
  });
}

// ==================== Admin 素材管理 ====================

/** 获取所有素材 */
export async function getAdminMaterialList(params?: {
  user_id?: number;
  type?: 1 | 2 | 3;
  page?: number;
  page_size?: number;
}) {
  return request<GEO.ApiResponse<{
    list: Array<{
      id: number;
      user_id: number;
      user_name: string;
      title: string;
      type: number;
      status: number;
      create_time: number;
    }>;
    total: number;
  }>>('/admin/material/list', {
    method: 'GET',
    params,
  });
}

/** 审核素材 */
export async function auditAdminMaterial(data: {
  id: number;
  status: 1 | 2;
  reason?: string;
}) {
  return request<GEO.ApiResponse>('/admin/material/audit', {
    method: 'POST',
    data,
  });
}

// ==================== Admin 发布管理 ====================

/** 获取所有发布记录 */
export async function getAdminPublishList(params?: {
  user_id?: number;
  user_name?: string;
  keyword?: string;
  platform_id?: number;
  material_type?: number;
  /** @deprecated 使用 keyword */
  product_title?: string;
  /** @deprecated 使用 platform_id */
  platform_name?: string;
  /** 1自媒体发布 2B2B 3媒体发布 */
  publish_channel_type?: 1 | 2 | 3;
  /** 0失败 1成功 2审核中 3审核拒绝 */
  status?: 0 | 1 | 2 | 3;
  page?: number;
  page_size?: number;
}) {
  return request<GEO.ApiResponse<{
    list: Array<{
      id: number;
      user_id: number;
      user_name: string;
      material_type: number;
      material_type_name?: string;
      material_id?: number;
      product_id: number;
      product_name: string;
      keyword_name: string;
      platform_id?: number;
      platform_name: string;
      publish_url: string;
      publish_time: number;
      publish_channel_type?: number;
      publish_channel_type_name?: string;
      status: number;
      status_text?: string;
      create_time?: number;
    }>;
    total: number;
  }>>('/admin/publish/list', {
    method: 'GET',
    params,
  });
}

/** 超管更新发布明细 */
export async function updateAdminPublish(data: {
  id: number;
  publish_url?: string;
  publish_time?: number;
  status?: 0 | 1 | 2 | 3;
}) {
  return request<GEO.ApiResponse>('/admin/publish/update', {
    method: 'POST',
    data,
  });
}

/** 超管删除发布明细 */
export async function deleteAdminPublish(id: number) {
  return request<GEO.ApiResponse>('/admin/publish/delete', {
    method: 'POST',
    data: { id },
  });
}

// ==================== Admin 账单管理 ====================

/** 获取所有用户流水 */
export async function getAdminFinanceLogs(params?: {
  user_id?: number;
  type?: 1 | 2 | 3 | 4;
  page?: number;
  page_size?: number;
}) {
  return request<GEO.ApiResponse<{
    list: Array<{
      id: number;
      user_id: number;
      user_name: string;
      type: number;
      type_text: string;
      amount: string;
      balance_before: string;
      balance_after: string;
      remark: string;
      create_time: number;
    }>;
    total: number;
  }>>('/admin/finance/logs', {
    method: 'GET',
    params,
  });
}

/** 获取Token消耗记录 */
export async function getAdminTokenLogs(params?: {
  user_id?: number;
  page?: number;
  page_size?: number;
}) {
  return request<GEO.ApiResponse<{
    list: Array<{
      id: number;
      user_id: number;
      user_name: string;
      token_amount: number;
      action: string;
      create_time: number;
    }>;
    total: number;
  }>>('/admin/finance/tokenLogs', {
    method: 'GET',
    params,
  });
}

// ==================== AI平台管理 ====================

/** 获取AI平台列表 */
export async function getAiPlatformList(params?: {
  page?: number;
  page_size?: number;
  name?: string;
  status?: number;
}) {
  return request<GEO.ApiResponse<{
    list: Array<{
      id: number;
      name: string;
      url: string;
      status: number;
      create_time: string;
      update_time: string;
    }>;
    total: number;
  }>>('/api/aiPlatform/list', {
    method: 'GET',
    params,
  });
}

/** 获取AI平台详情 */
export async function getAiPlatformDetail(id: number) {
  return request<GEO.ApiResponse<{
    id: number;
    name: string;
    url: string;
    status: number;
    create_time: string;
    update_time: string;
  }>>('/api/aiPlatform/detail', {
    method: 'GET',
    params: { id },
  });
}

/** 新增AI平台 */
export async function addAiPlatform(data: {
  name: string;
  url?: string;
  status?: number;
}) {
  return request<GEO.ApiResponse>('/api/aiPlatform/add', {
    method: 'POST',
    data,
  });
}

/** 编辑AI平台 */
export async function updateAiPlatform(data: {
  id: number;
  name?: string;
  url?: string;
  status?: number;
}) {
  return request<GEO.ApiResponse>('/api/aiPlatform/update', {
    method: 'POST',
    data,
  });
}

/** 删除AI平台 */
export async function deleteAiPlatform(id: number) {
  return request<GEO.ApiResponse>('/api/aiPlatform/delete', {
    method: 'POST',
    data: { id },
  });
}

// ==================== 媒体平台管理 ====================

/** 获取媒体平台列表 */
export async function getMediaPlatformList(params?: {
  page?: number;
  page_size?: number;
  name?: string;
  status?: number;
  publish_category?: number;
}) {
  return request<GEO.ApiResponse<{
    list: Array<{
      id: number;
      name: string;
      login_url: string;
      type: string;
      publish_category?: number;
      status: number;
    }>;
    total: number;
  }>>('/api/mediaPlatform/list', {
    method: 'GET',
    params,
  });
}

/** 获取媒体平台详情 */
export async function getMediaPlatformDetail(id: number) {
  return request<GEO.ApiResponse<{
    id: number;
    name: string;
    login_url: string;
    type: string;
    publish_category?: number;
    status: number;
  }>>('/api/mediaPlatform/detail', {
    method: 'GET',
    params: { id },
  });
}

/** 新增媒体平台 */
export async function addMediaPlatform(data: {
  name: string;
  login_url?: string;
  type?: string;
  publish_category?: number;
  status?: number;
}) {
  return request<GEO.ApiResponse>('/api/mediaPlatform/add', {
    method: 'POST',
    data,
  });
}

/** 编辑媒体平台 */
export async function updateMediaPlatform(data: {
  id: number;
  name?: string;
  login_url?: string;
  type?: string;
  publish_category?: number;
  status?: number;
}) {
  return request<GEO.ApiResponse>('/api/mediaPlatform/update', {
    method: 'POST',
    data,
  });
}

/** 删除媒体平台 */
export async function deleteMediaPlatform(id: number) {
  return request<GEO.ApiResponse>('/api/mediaPlatform/delete', {
    method: 'POST',
    data: { id },
  });
}

// ==================== 用户数据统计 ====================

/** 获取用户数据统计 */
export async function getUserStatistics(params?: {
  page?: number;
  page_size?: number;
  company_name?: string;
  user_type?: number;
}) {
  return request<GEO.ApiResponse<{
    list: Array<{
      user_id: number;
      username: string;
      company_name: string;
      user_type: number;
      user_type_name: string;
      keyword_total: number;
      inclusion_total: number;
      included_count: number;
      publish_count: number;
      last_check_time: number | null;
      platform_ratio: Array<{
        platform_id: number;
        platform_name: string;
        platform_url: string;
        total: number;
        included: number;
        not_included: number;
        ratio: number;
      }>;
    }>;
    total: number;
    platforms: Array<{
      id: number;
      name: string;
      url: string;
    }>;
  }>>('/agent/data/userStatistics', {
    method: 'GET',
    params,
  });
}

// ==================== 超管端-收录查询（仅超管） ====================

/** 获取某个公司的主词（parent_id=0） */
export async function getAgentMainKeywords(params: { user_id: number }) {
  return request<GEO.ApiResponse<{
    list: Array<{ id: number; keyword: string }>;
  }>>('/agent/inclusionQuery/mainKeywords', {
    method: 'GET',
    params,
  });
}

/** 下发收录查询任务（转发到外部服务） */
export async function enqueueBatchAiInclusion(data: {
  user_id: number;
  keyword_id: number;
  platform_id: number;
  full_query?: 0 | 1;
}) {
  return request<GEO.ApiResponse<{
    total: number;
    item_ids: number[];
  }>>('/agent/inclusionQuery/enqueueBatchAiInclusion', {
    method: 'POST',
    data,
  });
}

/** 代理端：获取下级发布明细 */
export async function getAgentPublishList(params?: {
  keyword?: string;
  platform_id?: number;
  material_type?: 1 | 2 | 3;
  /** 1自媒体发布 2B2B 3媒体发布 */
  publish_channel_type?: 1 | 2 | 3;
  status?: 0 | 1 | 2 | 3;
  page?: number;
  page_size?: number;
}) {
  return request<
    GEO.ApiResponse<{
      list: Array<{
        id: number;
        material_type: number;
        material_type_name?: string;
        product_id: number;
        material_id?: number;
        product_name: string;
        keyword_name: string;
        platform_name: string;
        account_name: string;
        owner_name: string;
        parent_name: string;
        publish_url: string;
        publish_time: number;
        publish_channel_type?: number;
        publish_channel_type_name?: string;
        status: number;
        status_text?: string;
      }>;
      total: number;
    }>
  >('/agent/publish/list', {
    method: 'GET',
    params,
  });
}

/** 代理端：获取下级发布账号 */
export async function getAgentPublishAccountList(params?: {
  material_id?: number;
  status?: number;
  remark?: string;
  page?: number;
  page_size?: number;
}) {
  return request<GEO.ApiResponse<{ list: any[]; total: number }>>('/agent/publishAccount/list', {
    method: 'GET',
    params,
  });
}

/** 代理端：更新下级发布账号备注 */
export async function updateAgentPublishAccountRemark(data: { id: number; remark?: string }) {
  return request<GEO.ApiResponse>('/agent/publishAccount/updateRemark', {
    method: 'POST',
    data,
  });
}

/** 代理端：删除下级发布账号 */
export async function deleteAgentPublishAccount(data: { id: number }) {
  return request<GEO.ApiResponse>('/agent/publishAccount/delete', {
    method: 'POST',
    data,
  });
}

/** 代理端：获取下级AI授权账号 */
export async function getAgentAiAccountList(params?: {
  material_id?: number;
  status?: number;
  remark?: string;
  page?: number;
  page_size?: number;
}) {
  return request<GEO.ApiResponse<{ list: any[]; total: number }>>('/agent/aiAccount/list', {
    method: 'GET',
    params,
  });
}

/** 代理端：更新下级AI授权账号备注 */
export async function updateAgentAiAccountRemark(data: { id: number; remark?: string }) {
  return request<GEO.ApiResponse>('/agent/aiAccount/updateRemark', {
    method: 'POST',
    data,
  });
}

/** 代理端：删除下级AI授权账号 */
export async function deleteAgentAiAccount(data: { id: number }) {
  return request<GEO.ApiResponse>('/agent/aiAccount/delete', {
    method: 'POST',
    data,
  });
}

/** 用户端：获取SaaS网站信息配置 */
export async function getSaasSiteInfo() {
  return request<GEO.ApiResponse<{
    site_name: string;
    logo: string;
    favicon: string;
    meta_keywords: string;
    meta_description: string;
    company_name: string;
    email: string;
    phone: string;
    address: string;
    working_hours: string;
    facebook: string;
    twitter: string;
    instagram: string;
    tiktok: string;
    whatsapp: string;
    template: string;
    primary_color: string;
    lang: string;
    footer_text: string;
  }>>('/api/saasSite/get', {
    method: 'GET',
  });
}

/** 用户端：更新SaaS网站信息配置 */
export async function updateSaasSiteInfo(data: {
  site_name?: string;
  logo?: string;
  favicon?: string;
  meta_keywords?: string;
  meta_description?: string;
  company_name?: string;
  email?: string;
  phone?: string;
  address?: string;
  working_hours?: string;
  facebook?: string;
  twitter?: string;
  instagram?: string;
  tiktok?: string;
  whatsapp?: string;
  template?: string;
  primary_color?: string;
  lang?: string;
  footer_text?: string;
}) {
  return request<GEO.ApiResponse>('/api/saasSite/update', {
    method: 'POST',
    data,
  });
}

/** 用户端：上传Logo（前端已压缩） */
export async function uploadSaasLogo(file: File) {
  const formData = new FormData();
  formData.append('file', file);
  return request<GEO.ApiResponse<{ url: string }>>('/api/saasSite/uploadLogo', {
    method: 'POST',
    data: formData,
  });
}

/** 用户端：上传Favicon（前端已压缩） */
export async function uploadSaasFavicon(file: File) {
  const formData = new FormData();
  formData.append('file', file);
  return request<GEO.ApiResponse<{ url: string }>>('/api/saasSite/uploadFavicon', {
    method: 'POST',
    data: formData,
  });
}

/** 用户端：内容列表 */
export async function getSaasContentList(categoryId: number) {
  return request<GEO.ApiResponse<any[]>>('/api/saasContent/list', { method: 'GET', params: { category_id: categoryId } });
}

/** 用户端：创建内容 */
export async function createSaasContent(data: any) {
  return request<GEO.ApiResponse>('/api/saasContent/create', { method: 'POST', data });
}

/** 用户端：更新内容 */
export async function updateSaasContent(data: any) {
  return request<GEO.ApiResponse>('/api/saasContent/update', { method: 'POST', data });
}

/** 用户端：删除内容 */
export async function deleteSaasContent(data: { id: number }) {
  return request<GEO.ApiResponse>('/api/saasContent/delete', { method: 'POST', data });
}

/** 用户端：上传内容缩略图 */
export async function uploadSaasNewsThumb(file: File) {
  const formData = new FormData();
  formData.append('file', file);
  return request<GEO.ApiResponse<{ url: string }>>('/api/saasContent/uploadThumb', {
    method: 'POST',
    data: formData,
  });
}

/** 用户端：内容 创建 & 更新 & 翻译统一入口 */
export async function saveSaasContent(data: any) {
  return request<GEO.ApiResponse<{ id: number }>>('/api/saasContent/save', { method: 'POST', data });
}

/** 用户端：AI生成SEO关键词和描述 */
export async function generateSaasContentSeo(data: { title: string; content: string }) {
  return request<GEO.ApiResponse<{ seo_title: string; meta_keywords: string; meta_description: string }>>('/api/saasContent/seoGenerate', { method: 'POST', data });
}

/** 用户端：Banner列表 */
export async function getSaasBannerList() {
  return request<GEO.ApiResponse<BannerItem[]>>('/api/saasBanner/list', { method: 'GET' });
}

/** SaaS：Banner 创建 & 更新 & 翻译统一入口 */
export async function saveSaasBanner(data: any) {
  return request<GEO.ApiResponse>('/api/saasBanner/save', { method: 'POST', data });
}

/** 用户端：删除Banner */
export async function deleteSaasBanner(data: { id: number }) {
  return request<GEO.ApiResponse>('/api/saasBanner/delete', { method: 'POST', data });
}

/** 用户端：Banner排序 */
export async function sortSaasBanner(data: { id: number; direction: string }) {
  return request<GEO.ApiResponse>('/api/saasBanner/sort', { method: 'POST', data });
}

/** 用户端：上传 Banner 图片 */
export async function uploadSaasBanner(file: File) {
  const formData = new FormData();
  formData.append('file', file);
  return request<GEO.ApiResponse<{ url: string }>>('/api/saasBanner/upload', {
    method: 'POST',
    data: formData,
  });
}

interface BannerItem {
  id: number;
  title: string;
  subtitle: string;
  image: string;
  link: string;
  sort: number;
  status: number;
  lang: string;
}

// ==================== SaaS 域名管理 ====================

export interface DomainItem {
  id: number;
  uid: number;
  domain: string;
  create_time: string;
}

/** 获取域名列表 */
export async function getSaasDomainList() {
  return request<GEO.ApiResponse<DomainItem[]>>('/api/saasDomain/list');
}

/** 添加域名 */
export async function createSaasDomain(domain: string) {
  return request<GEO.ApiResponse>('/api/saasDomain/create', {
    method: 'POST',
    data: { domain },
  });
}

/** 删除域名 */
export async function deleteSaasDomain(id: number) {
  return request<GEO.ApiResponse>('/api/saasDomain/delete', {
    method: 'POST',
    data: { id },
  });
}

// ==================== 翻译接口 ====================

/** 单条翻译结果 */
export interface TranslateItem {
  source_text: string;
  translations: Array<{
    code: string;
    name: string;
    text: string;
  }>;
}
